import { useLoadingStore, selectLoading } from '@/modules/shared/stores/loadingStore'
import * as React from 'react'
import { useContractForm } from '../../context/ContractFormContext'
import useContractService from '../../services/ContractService'
import { toast } from '@/lib/toasts/api'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { ContractDetailsModel } from '../../types/contracts'
import { useDrivers } from '@/modules/shared/context/drivers/DriversContext'
import {
  CivilLawCostsOfIncomeType,
  ContractType,
  TaxCostsType,
} from '../../types/contracts'

const ContractFooter = ({
  driversId,
  driverNames = [],
  companyId,
  close,
  contractId,
  anmexId,
  annexInitialStartDate,
}: {
  driversId: number | number[]
  driverNames?: string[]
  companyId: string
  close: () => void
  contractId?: string
  anmexId?: string
  annexInitialStartDate?: string
}) => {
  const { t } = useLocalize()
  const { data, isEditState, isAnnexState } = useContractForm()
  const formattedAnnexInitialDate =
    annexInitialStartDate?.split('T')[0] ?? annexInitialStartDate
  const { triggerRefresh } = useDrivers()
  const startLoading = useLoadingStore((s) => s.startLoading)
  const loading = useLoadingStore(selectLoading)
  const { createContract, createGroupedContract } = useContractService()

  type GroupedSaveResult = {
    driverId?: number
    DriverId?: number
    status?: string
    Status?: string
    errors?: Array<{ code?: string; Code?: string }>
    Errors?: Array<{ code?: string; Code?: string }>
  }

  const resolveResultStatus = (item: GroupedSaveResult) =>
    String(item?.status ?? item?.Status ?? '').toLowerCase()

  const resolveResultErrors = (item: GroupedSaveResult) =>
    (item?.errors ?? item?.Errors ?? []).map(
      (error) => error?.code ?? error?.Code ?? t('error.unknown'),
    )

  const submitData = async () => {
    const stopLoading = startLoading()

    const isCivilLawLike =
      data.type === ContractType.CivilLawAgreement ||
      data.type === ContractType.SelfEmployment

    const normalizedTaxCosts = Number(
      data.increasedTaxCosts ?? TaxCostsType.Normal,
    )
    const normalizedCivilLawCosts = Number(
      data.increasedCivilLawCostsOfIncome ?? CivilLawCostsOfIncomeType.Normal,
    )
    const normalizedTaxCostsForSave =
      !isCivilLawLike &&
      normalizedTaxCosts === TaxCostsType.Normal &&
      normalizedCivilLawCosts !== CivilLawCostsOfIncomeType.Normal
        ? normalizedCivilLawCosts
        : normalizedTaxCosts

    const formattedData = {
      ...data,
      id: !isEditState
        ? isAnnexState
          ? 0
          : data.id
        : isAnnexState
          ? anmexId
          : data.id,
      isAnnex: isAnnexState,
      startDate: data.startDate.split('T')[0],
      // initialLeaveConfigurationDay:
      //   isEditState && isAnnexState
      //     ? formattedAnnexInitialDate
      //     : data?.initialLeaveConfigurationDay,
      parentContractId: isAnnexState ? contractId : 0,
      increasedTaxCosts: normalizedTaxCostsForSave,
      increasedCivilLawCostsOfIncome: normalizedCivilLawCosts,
    }
    const isGrouped = Array.isArray(driversId)
    const selectedDriversIds = isGrouped
      ? driversId.map((driverId) => Number(driverId)).filter((driverId) => !Number.isNaN(driverId))
      : [Number(driversId)].filter((driverId) => !Number.isNaN(driverId))

    if (!selectedDriversIds.length) {
      toast.error({ text: t('contract.add-driver-failed') })
      stopLoading()
      return
    }

    const savePromise = isGrouped
      ? createGroupedContract({
          companyId,
          driversIds: selectedDriversIds,
          contractDetails: formattedData as ContractDetailsModel,
        })
      : createContract({
          companyId,
          driverId: selectedDriversIds[0],
          contractDetails: formattedData as ContractDetailsModel,
        })

    await savePromise
      .then((response: GroupedSaveResult[] | unknown) => {
        if (Array.isArray(response)) {
          const normalizedResponse = response as GroupedSaveResult[]
          const successCount = normalizedResponse.filter(
            (item) => resolveResultStatus(item) === 'success',
          ).length
          const totalCount = normalizedResponse.length
          const failedDriverLabels = normalizedResponse
            .map((item, index) => ({
              status: resolveResultStatus(item),
              label:
                driverNames?.[index] ??
                `${t('common.driver')} ${item?.driverId ?? item?.DriverId ?? index + 1}`,
              errors: resolveResultErrors(item),
            }))
            .filter((item) => item.status !== 'success')

          if (successCount === totalCount) {
            toast.success({ text: t('contract.saved-successfully') })
          } else if (successCount > 0) {
            toast.warning({
              text: `${t('contract.saved-successfully')} (${successCount}/${totalCount}). ${t('contract.add-driver-failed')}: ${failedDriverLabels.map((item) => `${item.label}${item.errors.length ? ` [${item.errors.join(', ')}]` : ''}`).join(', ')}`,
            })
          } else {
            toast.error({
              text: `${t('contract.add-driver-failed')}: ${failedDriverLabels.map((item) => `${item.label}${item.errors.length ? ` [${item.errors.join(', ')}]` : ''}`).join(', ')}`,
            })
            return
          }
        } else {
          toast.success({ text: t('contract.saved-successfully') })
        }
        close()
        triggerRefresh()
      })
      .catch((err) => {
        const errorMessage = err?.response?.data[0]?.code || t('error.unknown')
        toast.error({ text: `${t('contract.add-driver-failed')} - ${errorMessage}` })
      })
      .finally(() => {
        stopLoading()
      })
  }
  return (
    <div className="tw-flex tw-items-center tw-justify-end tw-border-t tw-border-base-300 tw-px-4">
      <div className="tw-my-4 tw-flex tw-items-center tw-gap-3">
        <button
          disabled={loading}
          type="button"
          onClick={() => submitData()}
          className={`tw-cursor-pointer tw-rounded-lg tw-bg-primary tw-p-3 tw-text-sm tw-font-medium tw-text-white hover:tw-opacity-80`}
        >
          {isEditState
            ? t('operation.save')
            : isAnnexState
              ? t('driverModule.add-annex')
              : t('contract.add-contract')}
        </button>
      </div>
    </div>
  )
}

export default ContractFooter
